import * as React from "react";

import {
  Box,
  BoxProps,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Switch,
  Text,
  chakra,
  useDisclosure,
} from "@chakra-ui/react";

import { Action, AddRounded, MinusRoundFilled } from "icons";
import { AuditCommentsSubsectionIndicator } from "../audit-comments";
import { ChartSubsectionInteractionsModal } from "./ChartSubsectionInteractionsModal";
import { Interaction } from "types";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import {
  useAuditPermissions,
  useChartTrackingContext,
  usePermissions,
} from "contexts";
import { AddCustomRowsModal } from "modules/provider/shared/custom-rows-modal";
import { ChartGenericElement, CustomRow } from "modules/charts-shared/types";

type ChartSubsectionHeaderProps = {
  children?: React.ReactNode;
  chartId: string;
  encounterId: string;
  sectionId: string;
  subsectionId: string;
  interactions?: Interaction[];
  subsectionLabel?: string;
  showNotesBtn?: boolean;
  showAuditsBtn?: boolean;
  subsectionNote?: string;
  subsectionOptions?: React.ReactElement;
  subsectionEndOptions?: React.ReactElement;
  showAddCustomRows?: boolean;
  customRowsOptions?: string[];
  isRequired?: boolean;
  showDeleteBtn?: boolean;
  showInteractions?: boolean;
  onDeleteSubSection?: () => void;
  showToggleInterpretation?: boolean;
  isInterpretationVisible?: boolean;
  onToggleInterpretation?: () => void;
} & BoxProps;

const BANNED_CHARTS_FROM_TRACKING = ["order-form"];

export function ChartSubsectionHeader(props: ChartSubsectionHeaderProps) {
  const {
    chartId,
    encounterId,
    sectionId,
    subsectionId,
    interactions,
    subsectionLabel = "",
    showAuditsBtn = true,
    children,
    subsectionNote,
    subsectionOptions,
    showAddCustomRows,
    customRowsOptions = [],
    showDeleteBtn = false,
    showInteractions = true,
    onDeleteSubSection = () => {},
    isRequired = true,
    subsectionEndOptions,
    showToggleInterpretation = false,
    isInterpretationVisible = false,
    onToggleInterpretation = () => {},
    ...boxProps
  } = props;

  const { isPastEncounter, isCurrentChartCompleted } =
    useChartTrackingContext();

  const { getValues, control, setValue } = useFormContext();

  const values = getValues();

  const chartInteractions = React.useMemo<Interaction[]>(() => {
    if (interactions) {
      return interactions;
    }

    switch (sectionId) {
      case "SEKG":
        const ekgItems: ChartGenericElement = { ...values?.ekg };
        return (ekgItems?.[subsectionId]?.interactions as Interaction[]) ?? [];
        break;
      case "STRE":
        const { treatments = {}, treatmentsIvStarts = {} } = values;

        const allTreatments: ChartGenericElement = {
          ...treatments,
          ...treatmentsIvStarts,
        };

        const matchedTreatment = Object.values(allTreatments).find(
          (treatment) => treatment?.orderId === subsectionId
        );
        return (matchedTreatment?.interactions as Interaction[]) ?? [];
      case "SIVM":
        const { ivStarts = {}, ivMedications = {}, medications = {} } = values;

        const allItems: ChartGenericElement = {
          ...ivStarts,
          ...ivMedications,
          ...medications,
        };

        const matchedItem = Object.values(allItems).find(
          (item) => item?.orderId === subsectionId
        );
        return (matchedItem?.interactions as Interaction[]) ?? [];
      default:
        return (values?.[subsectionId]?.interactions as Interaction[]) ?? [];
    }
  }, [interactions, sectionId, subsectionId, values]);

  const chartInteractionsDisclose = useDisclosure();

  const { scope } = usePermissions();
  const { isAuditor, isCommenter } = useAuditPermissions();

  const location = useLocation();
  const fromAudits = location.pathname.includes("audits");

  const fromSummary = location.pathname.includes("medical-records");

  const customRows = (values?.[subsectionId]?.customRows as CustomRow[]) ?? [];

  const addCustomRowsDisclosure = useDisclosure();

  /**
   * The following variable will determine if the tracking will be available for the chart.
   * Some charts already have the tracking available, so we don't need to track them again.
   */
  const canTrackInteractions = React.useMemo(() => {
    return !BANNED_CHARTS_FROM_TRACKING.some((value) =>
      location.pathname.split("/").includes(value)
    );
  }, [location.pathname]);

  const titleAccesory = React.useMemo(
    () =>
      ["SHPI", "SROS", "SPEX", "SCHC", "SASS"].includes(sectionId) && isRequired
        ? "*"
        : "",
    [sectionId, isRequired]
  );

  const handleOpenInteractionsModal = () => {
    chartInteractionsDisclose.onOpen();
  };

  const sectionTitle = children as React.ReactNode;
  const hasTitle = Boolean(titleAccesory) || Boolean(sectionTitle);

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding={hasTitle ? "0 1.25rem" : "0 .725rem"}
        {...boxProps}
      >
        <Box
          display="inline-flex"
          justifyContent="space-between"
          alignItems="center"
          position="relative"
        >
          <HStack spacing="20px">
            {hasTitle && (
              <Text as="h2" fontSize="1.125rem" fontWeight="600">
                {sectionTitle}{" "}
                <chakra.span color="red">{titleAccesory}</chakra.span>
              </Text>
            )}

            {!!subsectionOptions && React.cloneElement(subsectionOptions)}
            {showAuditsBtn &&
              fromAudits &&
              // Commenters must see/address audit comments even though the chart
              // is chart-`Completed` (audited charts always are), which otherwise
              // makes isCurrentChartCompleted true for anyone but the assigned
              // auditor and hides the comment indicator.
              (!isCurrentChartCompleted || isCommenter) &&
              (isAuditor || isCommenter) && (
                <AuditCommentsSubsectionIndicator
                  encounterId={encounterId}
                  chartCode={chartId}
                  sectionCode={sectionId}
                  subsectionCode={subsectionId}
                  subsectionLabel={subsectionLabel}
                  isFromSideBar={false}
                />
              )}
          </HStack>

          {subsectionNote && (
            <Text
              fontSize="15px"
              fontWeight="400"
              color="#9A9EA7"
              padding="0 1.25rem"
            >
              {subsectionNote}
            </Text>
          )}
        </Box>
        <Box
          display="inline-flex"
          justifyContent="space-between"
          alignItems="end"
          position="relative"
          gap={4}
        >
          {!!showAddCustomRows && (
            <>
              <IconButton
                aria-label="add-btn"
                variant="icon"
                size="lg"
                disabled={isPastEncounter}
                icon={
                  <Icon
                    as={AddRounded}
                    color={"blue"}
                    sx={{ h: "20px", w: "20px" }}
                    onClick={addCustomRowsDisclosure.onOpen}
                  />
                }
              />
              <AddCustomRowsModal
                {...addCustomRowsDisclosure}
                control={control}
                chartCode={chartId}
                sectionCode={sectionId}
                subsectionCode={subsectionId}
                subsectionLabel={subsectionLabel}
                customOptions={customRowsOptions}
                setValue={setValue}
                currentOrder={
                  customRows.length
                    ? Math.max(
                        ...customRows.map((customRow) => customRow.order)
                      )
                    : 0
                }
              />
            </>
          )}

          {!!subsectionEndOptions && React.cloneElement(subsectionEndOptions)}
          {showToggleInterpretation && (
            <Flex columnGap="1rem" alignItems="flex-end">
              <Switch
                size="lg"
                isChecked={Boolean(isInterpretationVisible)}
                onChange={() => onToggleInterpretation()}
                colorScheme="green"
              />
            </Flex>
          )}

          {!fromAudits &&
            showInteractions &&
            canTrackInteractions &&
            !fromSummary &&
            scope("chartinteractions").isAccessible && (
              <IconButton
                aria-label="chart-subsection-interaction-btn"
                variant="icon"
                size="lg"
                title={subsectionId}
                icon={
                  <Icon
                    as={Action}
                    color={
                      (chartInteractions ?? []).length > 0 ? "blue" : "gray.700"
                    }
                    sx={{ h: "55px", w: "55px" }}
                  />
                }
                onClick={handleOpenInteractionsModal}
              />
            )}
          {showDeleteBtn && (
            <Button
              variant="ghost"
              color="red"
              fontSize="1.0625rem"
              disabled={isPastEncounter}
              rightIcon={
                <Icon
                  borderRadius="1rem"
                  color="red"
                  backgroundColor="white"
                  as={MinusRoundFilled}
                />
              }
              onClick={onDeleteSubSection}
            >
              Remove
            </Button>
          )}
        </Box>
      </Box>

      {!fromAudits &&
        showInteractions &&
        canTrackInteractions &&
        !fromSummary &&
        scope("chartinteractions").isAccessible && (
          <ChartSubsectionInteractionsModal
            isOpen={chartInteractionsDisclose.isOpen}
            onClose={chartInteractionsDisclose.onClose}
            label={sectionTitle}
            interactions={chartInteractions || []}
          />
        )}
    </>
  );
}

export type { ChartSubsectionHeaderProps };
